import { AreaApi } from '@wiz-api/area-management'
import { ModelSceneApi } from '@wiz-api/scene-management'
import { DataCategoryApi } from '@wiz-api/data-category-management'
import { AbpApplicationConfigurationApi } from '@wiz-api/abp-application'
import {
  map,
  forEach,
  isNil,
  pick,
  cloneDeep,
  get,
  find,
  concat,
  fromPairs,
  isEmpty,
  findIndex,
  filter,
  isArray,
} from 'lodash'

const areaApi = new AreaApi()
const dataCategoryApi = new DataCategoryApi()
const abpApi = new AbpApplicationConfigurationApi()
const modelSceneApi = new ModelSceneApi()
/** ************************************************ 风险区域列表 **************************************** */
// 风险区域绘制实例列表
let riskAreaInstance = []
// 风险区域列表
let riskAreaList = []
// 风险区域图标列表
let areaIconList = []
// 风险等级与绘制区域颜色的映射
let riskLevelColor = {}
let cloudRender
let drawHandler
let clickAreaTimerOut = null
let sceneOptions = {
  isEnableGis: false,
  latitude: 0,
  longitude: 0,
  name: '',
  id: '',
  folderId: '',
  jsonData: '',
}

// 相关设置
const riskLevelMap =
  fromPairs(
    get(Wiz, 'globalSettings.dynamicModules.modules["visualization-cockpit"].config')?.riskLevelMap
  ) || []

// 获取场景详情
const getSceneDetail = async (id) => {
  const [err, res] = await modelSceneApi.get(id)
  let options = null
  if (!err && !isNil(res)) {
    options = pick(res, ['isEnableGis', 'latitude', 'longitude', 'name', 'id', 'folderId'])
  }
  const [jsonErr, jsonRes] = await modelSceneApi.getModelSecenRelatedJson({ id })
  if (!jsonErr) {
    options.jsonData = jsonRes
  }
  sceneOptions = cloneDeep(options)
}
// 初始化数据
const initAreaData = () => {
  riskAreaInstance = []
  riskAreaList = []
  areaIconList = []
  riskLevelColor = {}
  drawHandler = null
  clickAreaTimerOut = null
  sceneOptions = {
    isEnableGis: false,
    latitude: 0,
    longitude: 0,
    name: '',
    id: '',
    folderId: '',
    jsonData: '',
  }
}

// 打开风险区域详情
const openAreaDetail = (areaId) => {
  if (!areaId) {
    return
  }
  const openEvent = new CustomEvent('openComponent', {
    detail: {
      toCom: {
        code: 'DangerHiddenDetails',
        origin: 'tag',
        name: '风险隐患详情',
        originTagName: 'RiskArea',
      },
      type: 'tags',
      props: {
        areaId,
      },
    },
  })
  window.dispatchEvent(openEvent)
}

// 获取风险区域列表
const getAreaList = async (sceneId) => {
  const [err, res] = await areaApi.getList({
    modelId: sceneId,
    skipCount: 0,
    maxResultCount: 1000,
  })
  if (!err && res) {
    return res.items
  }
  return []
}

// 获取风险等级列表
const getDicDataByCode = async () => {
  const [configError, abpConfigList] = await abpApi.get()
  if (!configError && !isEmpty(abpConfigList)) {
    const [statusError, statusRes] = await dataCategoryApi.getByCode({
      code: abpConfigList.setting.values['AreaManagement.DataCategory.RiskLevel'],
    })
    if (!statusError) {
      forEach(statusRes?.dataItems, (item) => {
        riskLevelColor[item.id] = item.code
      })
    }
  }
}

// 绘制风险区域
const drawRiskArea = async (player, areaDetail, riskLevelId) => {
  if (isEmpty(areaDetail)) return
  const styleOptions = {
    type: 'riskArea',
    style: 'deafult',
  }
  if (!player.Native) return
  if (!isEmpty(riskLevelColor[riskLevelId])) {
    styleOptions.style = parseInt(riskLevelColor[riskLevelId].replace('#', ''), 16)
  }
  const pos = map(JSON.parse(areaDetail.coordinate), (item) => {
    return [item.x, item.y, item.z]
  })
  const baseOptions = {
    name: 'newArea',
    pos,
    height: areaDetail.height || 2.0,
    id: await getUUID(),
    editMode: false,
  }
  if (sceneOptions.isEnableGis) {
    let realH = await player.Native.GisPlatform.queryDEM([
      [sceneOptions.longitude, sceneOptions.latitude],
    ])
    realH = isArray(realH) ? 0 : realH
    baseOptions.llh = [sceneOptions.longitude, sceneOptions.latitude, realH]
  }
  const areaObject = await cloudRender.ElectoronicRail.createByPosition(baseOptions, styleOptions)
  // 缓存围栏绑定的数据id
  riskAreaInstance.push({ areaId: areaDetail.id, areaObject })
  setTimeout(() => {
    drawMarker(player, areaDetail)
  }, 200)
}

// 绘制标签
const drawMarker = async (player, areaDetail) => {
  if (!player.Native) return
  if (!areaDetail.isDisplayName) return
  const background = riskLevelMap[areaDetail.riskLevelName]
    ? riskLevelMap[areaDetail.riskLevelName]
    : 'sample'
  const index = findIndex(riskAreaInstance, ['areaId', areaDetail.id])
  const areaAabb = await player.Native.Node.getProperty(
    riskAreaInstance[index]?.areaObject?.data?.fenceObject,
    ['aabb']
  )
  const pos = Aabb.faceCenter(areaAabb.aabb, 'Z')
  const singleOption = {
    id: await getUUID(),
    pos,
    background,
    visible: true,
    nodeOffset: [0, -50],
    text: areaDetail.name,
    textColor: '#ffff00ff',
    hasTriangle: false,
  }
  const picId = await getUUID()
  const pictureOption = {
    id: picId,
    pos,
    backgroundUrl: `data://../apps/markers/${riskLevelMap[areaDetail.riskLevelName]}/left.png`,
    visible: true,
    nodeOffset: [-37, -48],
    imgSize: [49, 49],
  }
  // 文字
  const textRes = await cloudRender.Marker.createBySingleLine(singleOption)
  if (textRes.status) {
    const data = map(textRes.data, (item) => {
      return {
        id: item,
        areaId: areaDetail.id,
        type: 'single',
        areaDetail,
      }
    })
    areaIconList = concat(areaIconList, data)
  }
  // 图片
  const picRes = await cloudRender.Marker.createByPicture(pictureOption)
  if (picRes.status) {
    const data = map(picRes.data, (item) => {
      return {
        id: item,
        areaId: areaDetail.id,
        type: 'picture',
        areaDetail,
      }
    })
    areaIconList = concat(areaIconList, data)
  }
}

// 生成UUID
const getUUID = () => {
  const tempUrl = URL.createObjectURL(new Blob())
  const uuid = tempUrl.toString()
  URL.revokeObjectURL(tempUrl)
  return uuid.substring(uuid.lastIndexOf('/') + 1)
}

// 标签事件
const markerClick = (e) => {
  // 左键点击并且有id
  if (e.type === 1 && !isEmpty(e.markerId)) {
    const areaInfo = find(areaIconList, (item) => {
      return item.id === e.markerId
    })
    if (areaInfo) {
      // 移动到视角
      openAreaDetail(get(areaInfo, 'areaId'))
    }
  }
}

// 风险区域点击事件
const eventSelected = (e) => {
  if (e.node) {
    const areaInfo = find(riskAreaInstance, ['areaObject.data.fenceObject', e.node])
    // 不存在选中风险区域
    if (!areaInfo) {
      return
    }
    openAreaDetail(get(areaInfo, 'areaId'))
  }
}

// 创建风险区域
const create = async (player, sourceId, propCloudRender) => {
  if (!player.Native) {
    return
  }
  // 初始化数据
  initAreaData()
  cloudRender = propCloudRender
  await getSceneDetail(sourceId)
  // 生成风险区域
  drawHandler = new Promise(async (resolve) => {
    // 获取风险区域列表数据
    riskAreaList = await getAreaList(sourceId)
    riskAreaList = filter(riskAreaList, 'isDrawed')
    // 获取风险等级跟颜色对应
    await getDicDataByCode()
    await Promise.all(
      map(riskAreaList, (item) => {
        return drawRiskArea(player, item, item.riskLevelId)
      })
    )
    resolve()
  })
  cloudRender.Dispatch.on('Marker', markerClick)
  // 监听风险区域点击事件
  player.Native.Tools.EventBrowseLClick.connect(eventSelected)
}
// 销毁风险区域
const destroy = async (player) => {
  if (!player.Native) {
    return
  }
  await drawHandler
  if (!get(cloudRender, 'Marker')) return
  // 销毁图标
  cloudRender.Marker.destroyMarker(map(areaIconList, 'id'))
  // 销毁风险区域绘制
  cloudRender.ElectoronicRail.destroy(map(riskAreaInstance, 'areaObject.data.id'))
  // 取消标签的事件监听
  cloudRender.Dispatch.off('Marker', markerClick)
  // 取消风险区域事件监听
  player.Native.Tools.EventBrowseLClick.disconnect(eventSelected)
}

// 切换标签围栏显示隐藏
const toggleDisplay = async (player, type) => {
  await drawHandler
  // 设置围栏的显隐
  forEach(riskAreaInstance, async (item) => {
    await cloudRender.ElectoronicRail.update(item.areaObject?.data?.id, { visible: type })
  })
  cloudRender.Marker.updateBySingleLine(
    map(
      filter(areaIconList, (item) => {
        return item.type === 'single'
      }),
      (i) => {
        return {
          id: i.id,
          visible: type,
        }
      }
    )
  )
  cloudRender.Marker.updateByPicture(
    map(
      filter(areaIconList, (item) => {
        return item.type === 'picture'
      }),
      (i) => {
        return {
          id: i.id,
          visible: type,
        }
      }
    )
  )
}

// 隐藏风险区域及其标签
const hidden = async (player) => {
  await toggleDisplay(player, false)
}

// 重新显示风险区域及其标签
const display = async (player) => {
  await toggleDisplay(player, true)
}

// 移动视角到风险区域
const moveToArea = async (areaId) => {
  // 没有设置视角，那么默认移到区域的视角
  const area = find(riskAreaInstance, ['areaId', areaId])
  if (area) {
    const aabb = await player.Native.Node.getProperty(
      get(area, 'areaObject.data.fenceObject', []),
      ['aabb']
    )
    await cloudRender.Camera.moveCamera('Aabb', [aabb.aabb[0], aabb.aabb[1]])
  }
}
export default { create, destroy, hidden, display, moveToArea }
